import { useState, type KeyboardEvent } from 'react';

import { Link, useNavigate } from '@tanstack/react-router';

import type { Contact as ApiContact } from '@/api/generated';
import { contactsApi } from '@/api/queries/contacts';
import {
  Button,
  Checkbox,
  Field,
  Icon,
  IconButton,
  Input,
  ModalRoot,
  Select,
} from '@/components/ui';
import { cn } from '@/utils/cn';

interface NewContactState {
  titleBefore: string;
  firstName: string;
  lastName: string;
  titleAfter: string;
  primaryEmail: string;
  phone: string;
  residence: string;
  role: string;
  tags: string[];
  source: string;
  status: 'active' | 'inactive';
  doNotContact: boolean;
  consent: boolean;
  vip: boolean;
}

const INITIAL: NewContactState = {
  titleBefore: '',
  firstName: '',
  lastName: '',
  titleAfter: '',
  primaryEmail: '',
  phone: '',
  residence: '',
  role: '',
  tags: [],
  source: 'manual_entry',
  status: 'active',
  doNotContact: false,
  consent: false,
  vip: false,
};

const TITLE_BEFORE_OPTIONS = [
  { value: '', label: '—' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof.', label: 'Prof.' },
  { value: 'MUDr.', label: 'MUDr.' },
  { value: 'MDDr.', label: 'MDDr.' },
  { value: 'Mgr.', label: 'Mgr.' },
];

const SOURCE_OPTIONS = [
  { value: 'manual_entry', label: 'Manual entry' },
  { value: 'drasso', label: 'Drasso' },
  { value: 'ifmsa', label: 'IFMSA' },
  { value: 'eduportal', label: 'Eduportal' },
  { value: 'workshopy', label: 'Workshopy' },
];

const STATUS_OPTIONS: { value: NewContactState['status']; label: string; color: string }[] = [
  { value: 'active', label: 'Active', color: '#0F9D6B' },
  { value: 'inactive', label: 'Inactive', color: '#94A3B8' },
];

interface NewContactModalProps {
  open: boolean;
  onClose: () => void;
}

export function NewContactModal({ open, onClose }: NewContactModalProps) {
  const navigate = useNavigate();
  const create = contactsApi.useCreate();
  const [step, setStep] = useState<1 | 2>(1);
  const [data, setData] = useState<NewContactState>(INITIAL);
  const [tagDraft, setTagDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof NewContactState>(k: K, v: NewContactState[K]) =>
    setData((p) => ({ ...p, [k]: v }));

  const reset = () => {
    setStep(1);
    setData(INITIAL);
    setTagDraft('');
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const addTag = () => {
    const v = tagDraft.trim();
    if (!v || data.tags.includes(v)) {
      setTagDraft('');
      return;
    }
    update('tags', [...data.tags, v]);
    setTagDraft('');
  };

  const canContinue = !!data.primaryEmail.trim();

  const submit = async () => {
    setError(null);
    try {
      const contact: ApiContact = {
        profile: {
          titleBefore: data.titleBefore || undefined,
          firstName: data.firstName.trim(),
          lastName: data.lastName.trim(),
          titleAfter: data.titleAfter || undefined,
          residence: data.residence || undefined,
          languages: [],
        },
        contact: {
          primaryEmail: data.primaryEmail.trim(),
          emails: [],
          phones: data.phone
            ? [{ number: data.phone.trim(), type: 'mobile', primary: true }]
            : [],
          socialProfiles: [],
          addresses: [],
        },
        tags: data.tags.length > 0 ? data.tags : undefined,
        sources: [data.source],
        status: {
          active: data.status === 'active',
          doNotContact: data.doNotContact,
          vip: data.vip,
          verified: false,
        },
        consents: data.consent
          ? {
              marketing: {
                value: true,
                method: 'web',
                updatedAt: new Date().toISOString(),
              },
            }
          : undefined,
        // Server owns audit (createdAt/updatedAt/version) — placeholder so the
        // generated Contact type is satisfied; the API overwrites on create.
        audit: {
          createdAt: new Date().toISOString(),
          createdBy: '',
          updatedAt: new Date().toISOString(),
          updatedBy: '',
          version: 1,
        },
      };

      const created = await create.mutateAsync({ contact, source: data.source });
      close();
      if (created.id) navigate({ to: '/contacts/$id', params: { id: created.id } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create contact');
    }
  };

  return (
    <ModalRoot open={open} onClose={close} size="md" closeOnScrim>
      <div className="ds-modal-head">
        <div>
          <h2>New contact</h2>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
            Step {step} of 2 · {step === 1 ? 'Identity' : 'Tags & status'}
          </div>
        </div>
        <IconButton icon="x" label="Close" className="ds-modal-close" onClick={close} />
      </div>

      <div className="ds-modal-body">
        {step === 1 ? (
          <div className="ds-form-row">
            <div className="ds-form-grid-3">
              <Field label="Title before">
                <Select
                  value={data.titleBefore}
                  onChange={(e) => update('titleBefore', e.target.value)}
                  options={TITLE_BEFORE_OPTIONS}
                />
              </Field>
              <Field label="First name">
                <Input
                  value={data.firstName}
                  onChange={(e) => update('firstName', e.target.value)}
                  placeholder="Jana"
                  autoFocus
                />
              </Field>
              <Field label="Last name">
                <Input
                  value={data.lastName}
                  onChange={(e) => update('lastName', e.target.value)}
                  placeholder="Nováková"
                />
              </Field>
            </div>
            <div className="ds-form-grid-2">
              <Field label="Primary email" required>
                <Input
                  type="email"
                  value={data.primaryEmail}
                  onChange={(e) => update('primaryEmail', e.target.value)}
                  placeholder="jana.novakova@example.cz"
                />
              </Field>
              <Field label="Phone">
                <Input
                  type="tel"
                  value={data.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  placeholder="+420 …"
                />
              </Field>
            </div>
            <div className="ds-form-grid-2">
              <Field label="Role / specialization">
                <Input
                  value={data.role}
                  onChange={(e) => update('role', e.target.value)}
                  placeholder="Praktický lékař"
                />
              </Field>
              <Field label="Residence">
                <Input
                  value={data.residence}
                  onChange={(e) => update('residence', e.target.value)}
                  placeholder="Praha"
                />
              </Field>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
              After creating, you can add custom fields (date of birth, languages, bio, etc.)
              directly on the contact&apos;s page.{' '}
              <Link
                to="/contacts/new"
                onClick={close}
                style={{ color: 'var(--brand)', textDecoration: 'none', fontWeight: 500 }}
              >
                Need more fields? Open the full form →
              </Link>
            </div>
          </div>
        ) : (
          <div className="ds-form-row">
            <Field label="Tags" hint="Press Enter or comma to add a tag">
              <div
                className="ds-input"
                style={{
                  height: 'auto',
                  minHeight: 34,
                  padding: '4px 6px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 4,
                  alignItems: 'center',
                  cursor: 'text',
                }}
              >
                {data.tags.map((t) => (
                  <span
                    key={t}
                    className="ds-tag brand"
                    style={{ height: 22, paddingRight: 4 }}
                  >
                    {t}
                    <IconButton
                      icon="x"
                      iconSize={10}
                      size="sm"
                      label={`Remove ${t}`}
                      style={{ width: 16, height: 16, marginLeft: 4, color: 'currentColor' }}
                      onClick={() => update('tags', data.tags.filter((x) => x !== t))}
                    />
                  </span>
                ))}
                <input
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  placeholder={data.tags.length === 0 ? 'kategorie:lékař, vip…' : ''}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addTag();
                    } else if (e.key === 'Backspace' && !tagDraft && data.tags.length > 0) {
                      update('tags', data.tags.slice(0, -1));
                    }
                  }}
                  style={{
                    flex: 1,
                    minWidth: 100,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: 13,
                    color: 'inherit',
                    height: 26,
                  }}
                />
              </div>
            </Field>

            <Field label="Source">
              <Select
                value={data.source}
                onChange={(e) => update('source', e.target.value)}
                options={SOURCE_OPTIONS}
              />
            </Field>

            <Field label="Status">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={cn('chip-btn', data.status === opt.value && 'active')}
                    style={{ height: 34, justifyContent: 'center' }}
                    onClick={() => update('status', opt.value)}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: opt.color,
                      }}
                    />
                    {opt.label}
                  </button>
                ))}
              </div>
            </Field>

            <ToggleRow
              label="Do not contact"
              description="Excluded from every outreach, independent of the status"
              checked={data.doNotContact}
              onChange={(v) => update('doNotContact', v)}
            />
            <ToggleRow
              label="Marketing consent obtained"
              description="Required for GDPR-compliant outreach"
              checked={data.consent}
              onChange={(v) => update('consent', v)}
            />
            <ToggleRow
              label="Mark as VIP"
              description="Surfaces in the VIP saved view"
              checked={data.vip}
              onChange={(v) => update('vip', v)}
            />

            {error && (
              <div
                style={{
                  padding: '8px 12px',
                  background: 'var(--red-soft)',
                  color: 'var(--red)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 12.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Icon name="alert" size={14} />
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ds-modal-foot">
        {step === 2 && (
          <Button
            variant="ghost"
            leftIcon={<Icon name="chevronLeft" size={13} />}
            onClick={() => setStep(1)}
          >
            Back
          </Button>
        )}
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={close}>
          Cancel
        </Button>
        {step === 1 ? (
          <Button
            variant="primary"
            disabled={!canContinue}
            rightIcon={<Icon name="chevronRight" size={13} />}
            onClick={() => setStep(2)}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="primary"
            loading={create.isPending}
            leftIcon={!create.isPending ? <Icon name="check" size={13} /> : undefined}
            onClick={() => void submit()}
          >
            Create contact
          </Button>
        )}
      </div>
    </ModalRoot>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--surface-2)',
        cursor: 'pointer',
      }}
    >
      <Checkbox checked={checked} onChange={onChange} aria-label={label} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{label}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{description}</div>
      </div>
    </label>
  );
}
